/*
 * EntwurfEditor.tsx - Auftragsentwürfe bearbeiten.
 *
 * Created by Dr. Maximillian Dornseif 2022-03-25 in huWaWi3 26.3.0
 * Copyright (c) 2022, 2023 HUDORA GmbH
 */

import gql from 'graphql-tag';
import React from 'react';
import toast from 'react-hot-toast';
import { useMutation } from 'urql';
import { navigate } from 'wouter/use-location';

import { AUFTRAGSENTWURFPOSITIONEN_FRAGMENT, AUFTRAGSENTWURF_FRAGMENT } from '../../fragments';
import { EntityPageInner3 } from '../../lib/components/ke/EntityPageInner3';
import { KindPage3 } from '../../lib/components/ke/KindPage3';

export const EntwurfApp = () => {
  const renderEntityPage = (entity) => {
    return <EntityERP entity={entity} />;
  };

  return (
    <KindPage3
      basePath={'/wawi/ic'}
      componentPath={'e'}
      schemaName={'H3AuftragsEntwurf'} // commandBarItems={nearBarItems}
      title={'Auftrags-Entwürfe'}
      listQuery={AUFTRAGSENTWURF_LIST_QUERY}
      entityQuery={AUFTRAGSENTWURF_QUERY}
      hideExcept={[
        'designator',
        'kundennr',
        'keineWarnungen',
        'valide',
        'warnings',
        'created_via',
        'anlieferdatum',
        'lager',
        'auftragsnr_kunde',
        'created_at',
        'transportId',
        'erfasst_von',
        'errors',
      ]}
      renderEntityPage={renderEntityPage}
    />
  );
};

function EntityERP({ entity }) {
  const [mutationResult, mutateEntity] = useMutation(gql`
    mutation createAuftrag($nr: ID!) {
      h3AuftragsEntwurf2Auftrag(designator: $nr) {
        _keyStr
        id
        designator
      }
    }
  `);
  const { fetching } = mutationResult;

  // Daten per GraphQL Mutation an den Server senden.
  const uebertragen = async (e: any) => {
    const spinner = toast.loading(`Starte Übertragung`);
    const result = await mutateEntity({
      nr: entity.designator,
    } as any);
    toast.remove(spinner);
    if (result.error) {
      toast.error(`Fehler: ${result.error.message}`);
    } else {
      toast.success(`Erfolgreich Auftrag ${result.data.h3AuftragsEntwurf2Auftrag.designator} erstellt!`);
    }
    navigate(`/wawi/ic/a/${result.data.h3AuftragsEntwurf2Auftrag.designator}`);
  };

  const entityCommandBarItems = [
    {
      key: 'auftrag',
      text: 'Auftrag einspielen',
      ariaLabel: 'Auftrag einspielen',
      iconProps: {
        iconName: 'MSNVideosSolid',
      },
      iconOnly: false,
      disabled: entity.erledigt || !entity.valide || fetching,
      onClick: (e) => {
        uebertragen(e);
        return null;
      },
    },
  ];

  return (
    <EntityPageInner3
      entity={entity}
      schemaName={'H3AuftragsEntwurf'}
      mutation={AUFTRAGSENTWURF_EDIT_MUTATION}
      commandBarItems={entityCommandBarItems}
    />
  );
}
export const AUFTRAGSENTWURF_QUERY = gql`
  query h3AuftragsEntwurf($nr: ID!) {
    entity: h3AuftragsEntwurf(designator: $nr) {
      ...auftragsEntwurfFragment
      ...auftragsEntwurfPositionenFragment
    }
  }
  ${AUFTRAGSENTWURF_FRAGMENT}
  ${AUFTRAGSENTWURFPOSITIONEN_FRAGMENT}
`;
export const AUFTRAGSENTWURF_LIST_QUERY = gql`
  query AuftragsentwurfListQuery($cursor: String) {
    nodes: h3AuftragsEntwurfPages(after: $cursor, filter: { erledigt: { eq: false } }) {
      edges {
        cursor
        node {
          ...auftragsEntwurfFragment
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${AUFTRAGSENTWURF_FRAGMENT}
`;
const AUFTRAGSENTWURF_EDIT_MUTATION = gql`
  mutation AuftragsentwurfEditMutation($designator: ID!, $input: H3AuftragsEntwurfPatchInput!, $oldRev: ID!) {
    patchInfo: h3AuftragsEntwurfPatch(designator: $designator, input: $input, oldRev: $oldRev) {
      old {
        ...auftragsEntwurfFragment
        ...auftragsEntwurfPositionenFragment
      }
      new {
        ...auftragsEntwurfFragment
        ...auftragsEntwurfPositionenFragment
      }
      oldRev
      newRev
      duration
      success
      errors
      audit {
        created_at
        typ
        uid
        user
        text
        new
        old
      }
    }
  }
  ${AUFTRAGSENTWURF_FRAGMENT}
  ${AUFTRAGSENTWURFPOSITIONEN_FRAGMENT}
`;
